import { useState } from 'react';
import { ApiError } from '@/api/client';
import { useCreateService } from '@/api/queries';
import type { EventCard, ServiceKind } from '@/api/types';
import { ChoiceGroup, Option } from '@/components/kit/Controls';
import { Col, Form, NumberField, Row, TextAreaField, TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { EmptyState, Note } from '@/components/kit/Note';
import { useToast } from '@/components/kit/Toast';
import { date as fmtDate } from '@/lib/format';
import { SEATING, SELECTION, SERVICE_KIND } from '../state';

/**
 * Новая услуга.
 *
 * Тип выбирается первым и дальше не меняется: у трёх типов слишком
 * разный состав полей, и «сменить тип» означало бы стереть половину
 * заполненного. Проще завести новую.
 *
 * Тип выбирается радио, а не парным `.pick`: вариантов три
 * и у каждого пояснение — это ровно тот случай, для которого кит
 * велит брать радио.
 *
 * Поповера в окне нет: блок для билета выбирается списком опций.
 */
export function ServiceModal({
  event,
  initialKind,
  onClose,
}: {
  event: EventCard;
  initialKind: ServiceKind;
  onClose: () => void;
}) {
  const create = useCreateService(event.id);
  const toast = useToast();

  const [kind, setKind] = useState<ServiceKind>(initialKind);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // мастер-класс
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [capacity, setCapacity] = useState<number | null>(null);

  // билет
  const [blockId, setBlockId] = useState<string | null>(event.blocks[0]?.id ?? null);
  const [seating, setSeating] = useState<'free' | 'numbered'>('free');

  // шоукейс
  const [choreographer, setChoreographer] = useState('');
  const [selection, setSelection] = useState<'video' | 'none'>('none');

  // Билет без блока не заводится: вместимость зала — свойство блока,
  // а не товара. Потерять блок он может потом, когда блок удалят.
  const valid = Boolean(name.trim()) && (kind !== 'ticket' || Boolean(blockId));

  const submit = () => {
    const common = { kind, name: name.trim(), description: description.trim() || null };

    const body =
      kind === 'masterclass'
        ? { ...common, startsAt: startsAt.trim() || null, endsAt: endsAt.trim() || null, capacity }
        : kind === 'ticket'
          ? { ...common, blockId, seating, capacity }
          : { ...common, choreographer: choreographer.trim() || null, selection };

    create.mutate(body, {
      onSuccess: () => {
        toast.show(`${SERVICE_KIND[kind].label} «${name.trim()}» заведён`);
        onClose();
      },
      onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не завелась'),
    });
  };

  return (
    <Modal
      title="Новая услуга"
      onClose={onClose}
      size="wide"
      confirm={{ label: 'Завести', onClick: submit, disabled: !valid || create.isPending }}
    >
      <div className="field">
        <span className="lbl">Тип</span>
        <Option
          radio
          checked={kind === 'masterclass'}
          onToggle={() => setKind('masterclass')}
          title="Мастер-класс"
          description="Имя хореографа и стиль пишутся прямо в названии"
        />
        <Option
          radio
          checked={kind === 'ticket'}
          onToggle={() => setKind('ticket')}
          title="Билет зрителя"
          description="Всегда на один блок: вместимость зала — свойство блока"
        />
        <Option
          radio
          checked={kind === 'showcase'}
          onToggle={() => setKind('showcase')}
          title="Шоукейс"
          description="Показательное выступление, с отбором по видео или без"
        />
      </div>

      <Form>
        <TextField
          label="Название"
          value={name}
          onValue={setName}
          placeholder={
            kind === 'masterclass'
              ? 'Hip-Hop PRO · Артур Ким'
              : kind === 'ticket'
                ? 'Билет зрителя · PART 1'
                : 'Постановочный шоукейс'
          }
        />

        {kind === 'masterclass' ? (
          <>
            <Row>
              <Col fix>
                <TextField
                  label="Начало"
                  placeholder="09:00"
                  size={8}
                  value={startsAt}
                  onValue={setStartsAt}
                />
              </Col>
              <Col fix>
                <TextField
                  label="Окончание"
                  placeholder="10:20"
                  size={8}
                  value={endsAt}
                  onValue={setEndsAt}
                />
              </Col>
              <Col>
                <NumberField label="Вместимость" value={capacity} onValue={setCapacity} />
              </Col>
            </Row>
            <Note>
              Время можно не задавать: расписание классов проясняется позже. Вместимость —
              отдельный лимит: класс может закончиться, даже если в пакетах ещё есть места
            </Note>
          </>
        ) : null}

        {kind === 'ticket' ? (
          <>
            <div className="field">
              <span className="lbl">Блок</span>
              {event.blocks.length ? (
                event.blocks.map((b) => (
                  <Option
                    key={b.id}
                    radio
                    checked={blockId === b.id}
                    onToggle={() => setBlockId(b.id)}
                    title={b.name}
                    description={fmtDate(b.date)}
                  />
                ))
              ) : (
                <EmptyState>
                  Блоков ещё нет. Билет привязывается к блоку — сначала соберите программу
                </EmptyState>
              )}
            </div>

            <Row>
              <Col>
                <div className="field">
                  <span className="lbl">Рассадка</span>
                  <ChoiceGroup
                    value={seating}
                    onChange={setSeating}
                    items={[
                      { value: 'free', label: SEATING.free },
                      { value: 'numbered', label: SEATING.numbered },
                    ]}
                  />
                </div>
              </Col>
              <Col>
                <NumberField label="Вместимость" value={capacity} onValue={setCapacity} />
              </Col>
            </Row>
          </>
        ) : null}

        {kind === 'showcase' ? (
          <>
            <TextField
              label="Хореограф"
              value={choreographer}
              onValue={setChoreographer}
              placeholder="Приглашённый хореограф"
            />
            <div className="field">
              <span className="lbl">Отбор</span>
              <ChoiceGroup
                value={selection}
                onChange={setSelection}
                items={[
                  { value: 'video', label: SELECTION.video },
                  { value: 'none', label: SELECTION.none },
                ]}
              />
            </div>
            <Note>У шоукейса вместимости нет: на сцену выходит отобранный состав</Note>
          </>
        ) : null}

        <TextAreaField
          label="Описание"
          rows={3}
          value={description}
          onValue={setDescription}
        />
      </Form>
    </Modal>
  );
}
